import { Voucher, VoucherLine, VoucherType, Ledger, AuditEntry, AuditAction, AuditFieldChange } from "./types";

// ========================
// VOUCHER NUMBER GENERATION
// ========================

const voucherPrefixes: Record<VoucherType, string> = {
  Sales: "SAL",
  Purchase: "PUR",
  Receipt: "REC",
  Payment: "PAY",
  Journal: "JRN",
  Contra: "CNT",
  "Credit Note": "CRN",
  "Debit Note": "DRN",
  "Stock Journal": "STK",
  "Delivery Note": "DLV",
  "Receipt Note": "RCN",
  "Physical Stock": "PST",
};

const defaultStartingNumbers: Record<string, number> = {
  SAL: 1001,
  PUR: 1001,
  REC: 1001,
  PAY: 1001,
  JRN: 1001,
  CNT: 1001,
  CRN: 1001,
  DRN: 1001,
  STK: 1001,
  DLV: 1001,
  RCN: 1001,
  PST: 1001,
};

export function generateVoucherNumber(type: VoucherType, existingVouchers: Voucher[] = []): string {
  const prefix = voucherPrefixes[type] || "VOU";
  
  // Find all existing vouchers with this prefix to determine the next sequential number
  const prefixRegex = new RegExp(`^${prefix}-(\\d+)`, "i");
  let maxNum = defaultStartingNumbers[prefix] - 1;

  for (const v of existingVouchers) {
    if (!v.voucherNumber) continue;
    const match = v.voucherNumber.match(prefixRegex);
    if (match && match[1]) {
      const parsed = parseInt(match[1], 10);
      if (!isNaN(parsed) && parsed > maxNum) {
        maxNum = parsed;
      }
    }
  }

  const nextNum = maxNum + 1;
  return `${prefix}-${String(nextNum).padStart(4, "0")}`;
}

// ========================
// FINANCIAL YEAR & PERIOD LOCKING
// ========================

export function getIndianFinancialYear(dateInput: string | Date = new Date()): {
  label: string;
  startDate: string;
  endDate: string;
} {
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const month = d.getMonth() + 1; // 1 - 12
  const year = d.getFullYear();

  let startYear: number;
  let endYear: number;

  if (month >= 4) {
    startYear = year;
    endYear = year + 1;
  } else {
    startYear = year - 1;
    endYear = year;
  }

  const shortEnd = String(endYear).slice(-2);
  return {
    label: `FY ${startYear}-${shortEnd}`,
    startDate: `${startYear}-04-01`,
    endDate: `${endYear}-03-31`,
  };
}

export function validatePeriodLock(
  transactionDate: string,
  periodLockDate?: string | null,
  userRole?: string
): { allowed: boolean; warning?: string; error?: string } {
  if (!periodLockDate) return { allowed: true };

  const txDate = transactionDate.slice(0, 10);
  const lockDate = periodLockDate.slice(0, 10);

  if (txDate <= lockDate) {
    // Only company_admin or ca can post/edit in a locked period
    if (userRole === "company_admin" || userRole === "ca" || userRole === "super_admin") {
      return {
        allowed: true,
        warning: `Notice: This entry is dated ${txDate}, which is in a closed accounting period (locked on ${lockDate}). Authorized administrator override applied.`,
      };
    }
    return {
      allowed: false,
      error: `Accounting period is locked up to ${lockDate}. Backdated entries on or before this date are restricted. Contact your Company Admin or CA.`,
    };
  }

  return { allowed: true };
}

// ========================
// VOUCHER-SPECIFIC ACCOUNTING VALIDATIONS
// ========================

export function validateContraVoucher(
  debitGroup: string,
  creditGroup: string,
  amount: number
): { valid: boolean; error?: string } {
  const cashBankGroups = ["Cash-in-Hand", "Bank Accounts"];
  if (!cashBankGroups.includes(debitGroup)) {
    return { valid: false, error: "Contra debit account must be Cash-in-Hand or Bank Accounts." };
  }
  if (!cashBankGroups.includes(creditGroup)) {
    return { valid: false, error: "Contra credit account must be Cash-in-Hand or Bank Accounts." };
  }
  if (amount <= 0) {
    return { valid: false, error: "Contra transfer amount must be greater than zero." };
  }
  return { valid: true };
}

export function validatePaymentVoucher(
  creditGroup: string,
  totalAmount: number
): { valid: boolean; error?: string } {
  const cashBankGroups = ["Cash-in-Hand", "Bank Accounts"];
  if (!cashBankGroups.includes(creditGroup)) {
    return { valid: false, error: "Payment credit account (Paid From) must be a Cash or Bank account." };
  }
  if (totalAmount <= 0) {
    return { valid: false, error: "Payment amount must be greater than zero." };
  }
  return { valid: true };
}

export function validateReceiptVoucher(
  debitGroup: string,
  totalAmount: number
): { valid: boolean; error?: string } {
  const cashBankGroups = ["Cash-in-Hand", "Bank Accounts"];
  if (!cashBankGroups.includes(debitGroup)) {
    return { valid: false, error: "Receipt debit account (Received Into) must be a Cash or Bank account." };
  }
  if (totalAmount <= 0) {
    return { valid: false, error: "Receipt amount must be greater than zero." };
  }
  return { valid: true };
}

// ========================
// DOUBLE-ENTRY VALIDATION
// ========================

export function validateDoubleEntry(lines: VoucherLine[]): { valid: boolean; error?: string } {
  if (lines.length < 2) {
    return { valid: false, error: "Minimum 2 ledger entries required for double-entry" };
  }

  const totalDebit = lines.reduce((sum, l) => sum + l.debit, 0);
  const totalCredit = lines.reduce((sum, l) => sum + l.credit, 0);
  const diff = Math.abs(totalDebit - totalCredit);

  if (diff > 0.01) {
    return { valid: false, error: `Debit (₹${totalDebit.toFixed(2)}) ≠ Credit (₹${totalCredit.toFixed(2)}). Difference: ₹${diff.toFixed(2)}` };
  }

  for (const line of lines) {
    if (line.debit === 0 && line.credit === 0) {
      return { valid: false, error: `Line for ${line.ledgerName} has zero amount` };
    }
    if (line.debit > 0 && line.credit > 0) {
      return { valid: false, error: `Line for ${line.ledgerName} cannot have both debit and credit` };
    }
  }

  return { valid: true };
}

// ========================
// AUTO DOUBLE-ENTRY TEMPLATES
// ========================

export interface AutoEntryTemplate {
  type: VoucherType;
  debitGroup: string;
  creditGroup: string;
  description: string;
}

export const AUTO_ENTRY_TEMPLATES: AutoEntryTemplate[] = [
  { type: "Sales", debitGroup: "Sundry Debtors", creditGroup: "Sales Accounts", description: "Debit: Customer Ledger → Credit: Sales Ledger" },
  { type: "Purchase", debitGroup: "Purchase Accounts", creditGroup: "Sundry Creditors", description: "Debit: Purchase Ledger → Credit: Supplier Ledger" },
  { type: "Receipt", debitGroup: "Bank Accounts", creditGroup: "Sundry Debtors", description: "Debit: Bank/Cash → Credit: Customer Ledger" },
  { type: "Payment", debitGroup: "Sundry Creditors", creditGroup: "Bank Accounts", description: "Debit: Supplier Ledger → Credit: Bank/Cash" },
  { type: "Contra", debitGroup: "Bank Accounts", creditGroup: "Cash-in-Hand", description: "Debit: Bank → Credit: Cash (or vice versa)" },
];

export function createAutoVoucherLines(
  type: VoucherType,
  amount: number,
  debitLedger: { id: string; name: string },
  creditLedger: { id: string; name: string },
  gstLines?: { ledgerId: string; ledgerName: string; amount: number }[]
): VoucherLine[] {
  const lines: VoucherLine[] = [
    { id: crypto.randomUUID(), ledgerId: debitLedger.id, ledgerName: debitLedger.name, debit: amount, credit: 0 },
    { id: crypto.randomUUID(), ledgerId: creditLedger.id, ledgerName: creditLedger.name, debit: 0, credit: amount - (gstLines?.reduce((s, g) => s + g.amount, 0) || 0) },
  ];

  if (gstLines) {
    for (const gst of gstLines) {
      lines.push({
        id: crypto.randomUUID(),
        ledgerId: gst.ledgerId,
        ledgerName: gst.ledgerName,
        debit: 0,
        credit: gst.amount,
      });
    }
  }

  return lines;
}

// ========================
// LEDGER BALANCE UPDATES
// ========================

export function updateLedgerBalance(ledger: Ledger, debitAmount: number, creditAmount: number): Ledger {
  const nature = ledger.nature;
  // Assets & Expenses increase with Debit
  // Liabilities, Income & Equity increase with Credit
  const isDebitNature = nature === "Asset" || nature === "Expense";

  let balance = ledger.currentBalance;
  if (ledger.currentBalanceType === "Dr") {
    balance = ledger.currentBalance;
  } else {
    balance = -ledger.currentBalance;
  }

  balance += debitAmount - creditAmount;

  return {
    ...ledger,
    currentBalance: Math.abs(balance),
    currentBalanceType: balance >= 0 ? "Dr" : "Cr",
    updatedAt: new Date().toISOString(),
  };
}

// ========================
// AUDIT TRAIL
// ========================

let auditCounter = 100;

export function getClientIP(): string {
  // In a real app, IP would come from the server
  // For demo, we simulate common Indian ISP IPs
  const ips = ["103.21.58.193", "49.36.128.72", "223.226.151.30", "106.51.72.144", "182.69.33.205", "117.96.40.88"];
  return ips[Math.floor(Math.random() * ips.length)];
}

export function diffObjects(oldObj: Record<string, unknown>, newObj: Record<string, unknown>): AuditFieldChange[] {
  const changes: AuditFieldChange[] = [];
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  for (const key of allKeys) {
    const oldVal = String(oldObj[key] ?? "");
    const newVal = String(newObj[key] ?? "");
    if (oldVal !== newVal) {
      changes.push({ field: key, oldValue: oldVal, newValue: newVal });
    }
  }
  return changes;
}

// Track version numbers per entity
const entityVersions: Record<string, number> = {};

export function getNextVersion(entityId: string): number {
  entityVersions[entityId] = (entityVersions[entityId] || 0) + 1;
  return entityVersions[entityId];
}

export function createAuditEntry(
  userId: string,
  userName: string,
  userRole: string,
  action: AuditAction,
  module: string,
  entityId: string,
  entityRef: string,
  details: string,
  previousData?: unknown,
  newData?: unknown,
  changes?: AuditFieldChange[]
): AuditEntry {
  return {
    id: `AUD-${++auditCounter}`,
    timestamp: new Date().toISOString(),
    userId,
    userName,
    userRole,
    action,
    module,
    entityId,
    entityRef,
    details,
    ipAddress: getClientIP(),
    sessionId: `sess-${userId}-${Date.now().toString(36)}`,
    previousData: previousData ? JSON.stringify(previousData) : undefined,
    newData: newData ? JSON.stringify(newData) : undefined,
    changes,
    version: getNextVersion(entityId),
  };
}

// ========================
// PERMISSION CHECKS FOR ACCOUNTING
// ========================

export interface AccountingPermissions {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
}

export function getAccountingPermissions(role: string, module: string): AccountingPermissions {
  switch (role) {
    case "super_admin":
      return { canCreate: false, canEdit: false, canDelete: true, canApprove: false };
    case "company_admin":
      return { canCreate: true, canEdit: true, canDelete: true, canApprove: true };
    case "ca":
      return {
        canCreate: true,
        canEdit: true,
        canDelete: false,
        canApprove: true,
      };
    case "accountant":
      return {
        canCreate: true,
        canEdit: module === "Ledger",
        canDelete: false,
        canApprove: false,
      };
    default:
      return { canCreate: false, canEdit: false, canDelete: false, canApprove: false };
  }
}
